import React, { useEffect } from "react";
import useProductName from "brand-customization/useProductName";
import { DocumentTitle, PageHeader, Spinner } from "components/common";
import { useStore } from "stores/connect";
import usePaginationQueryParameter from "hooks/usePaginationQueryParameter";
import ForwarderDocs from "forwarder/util/ForwarderDocs";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import { TELEMETRY_EVENT_TYPE } from "telemetry/Constants";
import LicenseCheck from "license/LicenseCheck";
import { LICENSE_SUBJECTS } from "license/constants";
import InputProfilesListComponent from "./input-profiles/InputProfilesListComponent";
import InputProfilesStore, { InputProfilesActions } from "./stores/InputProfilesStore";
import ForwardersStore, { ForwardersActions } from "./stores/ForwardersStore";
import EmptyInputProfileListComponent from "./input-profiles/EmptyInputProfileListComponent";
import withIsPermitted from "./util/withIsPermitted";
import ForwarderPageNavigation from "./ForwarderPageNavigation";
const REQUIRED_PERMISSIONS = [
  "forwarders:read",
  "inputprofiles:read",
  "forwarderinputs:read"
];
const InputProfilesPage = () => {
  const productName = useProductName();
  const { page, pageSize, resetPage } = usePaginationQueryParameter();
  const { inputProfiles, pagination } = useStore(InputProfilesStore);
  const { all: forwarders } = useStore(ForwardersStore);
  const sendTelemetry = useSendTelemetry();
  useEffect(() => {
    InputProfilesActions.list({ page, pageSize });
    ForwardersActions.listAll();
  }, [page, pageSize]);
  const _reloadInputProfileList = () => {
    InputProfilesActions.list({
      page,
      pageSize,
      query: pagination.query
    });
  };
  const _saveForwardersAssignment = (updatedForwarders) => {
    sendTelemetry(TELEMETRY_EVENT_TYPE.FORWARDER.INPUTPROFILE_FORWARDER_ASSIGNED, {
      app_pathname: "forwarder",
      app_section: "input-profile"
    });
    const toBeUpdatedForwarders = [];
    updatedForwarders.forEach((uf) => {
      const forwarder = forwarders.find((f) => f.id === uf.forwarderId);
      if (forwarder) {
        toBeUpdatedForwarders.push({ ...forwarder, input_profile_id: uf.inputProfileId });
      }
    });
    ForwardersActions.updates(toBeUpdatedForwarders);
  };
  const isLoading = () => !inputProfiles || !forwarders;
  const handleQueryChange = (newPage, newPageSize, query) => {
    resetPage();
    InputProfilesActions.list({
      page: newPage,
      pageSize: newPageSize,
      query
    });
  };
  const loadContent = () => inputProfiles.length === 0 && pagination.query === "" ? /* @__PURE__ */ React.createElement(EmptyInputProfileListComponent, null) : /* @__PURE__ */ React.createElement(
    InputProfilesListComponent,
    {
      inputProfiles,
      forwarders,
      onForwardersAssign: _saveForwardersAssignment,
      onInputProfileUpdate: _reloadInputProfileList,
      pagination,
      onQueryChange: handleQueryChange
    }
  );
  return /* @__PURE__ */ React.createElement(DocumentTitle, { title: "Input Profiles" }, /* @__PURE__ */ React.createElement(ForwarderPageNavigation, null), /* @__PURE__ */ React.createElement(
    PageHeader,
    {
      title: "Input Profiles",
      documentationLink: {
        title: "Input profile documentation",
        path: ForwarderDocs.INPUT_PROFILE
      }
    },
    /* @__PURE__ */ React.createElement("span", null, "List of Input Profiles created in this ", productName, " instance.")
  ), /* @__PURE__ */ React.createElement(
    LicenseCheck,
    {
      licenseSubject: LICENSE_SUBJECTS.forwarder,
      displayWarningContainer: true,
      featureName: "Forwarder",
      bsStyle: "danger"
    }
  ), isLoading() ? /* @__PURE__ */ React.createElement(Spinner, null) : loadContent());
};
export default withIsPermitted(InputProfilesPage, REQUIRED_PERMISSIONS);
